import type { StarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import type { RecommendedGroup } from 'src/app/pages/starterDashboardV2/shared/types';
import type { EnrichedReadyToSharePlatformPostV2 } from 'src/app/shared/api/readyToShare';

export type PostConfigRow = Readonly<{
	requiresRecommendedGroup: boolean;
	includeMessage: boolean;
	requiresGroupSelection?: boolean;
	desktopEnabled: boolean;
}>;

export const POST_CONFIGS: Readonly<Record<EnrichedReadyToSharePlatformPostV2['__typename'], PostConfigRow>> = {
	ReadyToShareFacebookPost: {
		requiresRecommendedGroup: false,
		includeMessage: true,
		desktopEnabled: true,
	},
	ReadyToShareFacebookGroupsPost: {
		requiresRecommendedGroup: true,
		includeMessage: true,
		requiresGroupSelection: true,
		desktopEnabled: true,
	},
	ReadyToShareFacebookStory: {
		requiresRecommendedGroup: false,
		includeMessage: false,
		desktopEnabled: false,
	},
	ReadyToShareInstagramPost: {
		requiresRecommendedGroup: false,
		includeMessage: true,
		desktopEnabled: false,
	},
	ReadyToShareInstagramStory: {
		requiresRecommendedGroup: false,
		includeMessage: false,
		desktopEnabled: false,
	},
	ReadyToShareRedditGroupsPost: {
		requiresRecommendedGroup: true,
		includeMessage: true,
		requiresGroupSelection: true,
		desktopEnabled: true,
	},
};

function extractBodyFromV2Post(post: EnrichedReadyToSharePlatformPostV2): string {
	if ('body' in post) return post.body;
	return '';
}

export type MappedSharingResource = {
	message: string;
	shareUrl: string;
	previewImageUrl: string;
	recommendedGroup: RecommendedGroup | undefined;
	petitionTitle: string;
};

export function extractSharingResource(
	post: EnrichedReadyToSharePlatformPostV2,
	options: {
		petition: StarterDashboardV2PageData['petition'];
		recommendedGroup?: RecommendedGroup;
	},
): MappedSharingResource {
	const { petition, recommendedGroup } = options;

	const config = POST_CONFIGS[post.__typename];

	// The middle-end already fetches the correct image variant and mapPost() filters out
	// processing images, so post.images[0] is always the right image for image-based posts.
	// Group posts (no images field) fall back to the petition photo.
	const previewImageUrl = 'images' in post ? (post.images[0]?.url ?? '') : (petition.photo?.petitionLarge.url ?? '');

	const message = config.includeMessage ? extractBodyFromV2Post(post) : '';

	return {
		message,
		previewImageUrl,
		shareUrl:
			'starterPetitionUrl' in post
				? post.starterPetitionUrl
				: `https://www.change.org/p/${encodeURIComponent(petition.slug)}`,
		recommendedGroup,
		petitionTitle: petition.ask,
	};
}
